import Dexie, { type EntityTable } from "dexie";

export interface CachedProductUnit {
  id: string;
  name: string;
  multiplier: number;
  barcode?: string | null;
  salePrice?: number | null;
}

export interface CachedProduct {
  id: string;
  name: string;
  sku: string;
  barcode?: string | null;
  baseUnit: string;
  businessVertical?: string | null;
  category?: string | null;
  subcategory?: string | null;
  salePrice: number;
  avgCost: number;
  stockQty: number;
  minStockQty: number;
  trackExpiry?: boolean;
  nextExpiryDate?: string | null;
  units: CachedProductUnit[];
}

export interface CachedCustomer {
  id: string;
  name: string;
  phone?: string | null;
}

export interface CachedSupplier {
  id: string;
  name: string;
  legalName?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  taxCode?: string | null;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  representativeName?: string | null;
  representativePhone?: string | null;
  note?: string | null;
}

export interface LocalSale {
  id: string;
  code: string;
  total: number;
  paymentMethod: "CASH" | "BANK_TRANSFER" | "DEBT";
  createdAt: string;
  syncStatus: "LOCAL" | "PENDING" | "SYNCED" | "FAILED";
}

export interface LocalMoneyMovement {
  id: string;
  direction: "IN" | "OUT";
  amount: number;
  category: string;
  accountType: "CASH" | "BANK";
  createdAt: string;
  syncStatus: "LOCAL" | "PENDING" | "SYNCED";
}

export interface SyncQueueItem {
  id: string;
  tenantId: string;
  storeId: string;
  operation: string;
  type?:
    | "SALE"
    | "RECEIVE_STOCK"
    | "STOCK_ADJUSTMENT"
    | "CASH_MOVEMENT"
    | "DAILY_CLOSING";
  endpoint: string;
  payload: unknown;
  idempotencyKey: string;
  createdAt: string;
  status: "PENDING" | "SYNCING" | "SYNCED" | "FAILED";
  attemptCount: number;
  lastAttemptAt?: string;
  lastError?: string;
  error?: string;
}

export interface MetaRecord {
  key: string;
  value: unknown;
}

export interface CachedDebt {
  counterpartyType: "CUSTOMER" | "SUPPLIER";
  counterpartyId: string;
  name: string;
  phone?: string | null;
  balance: number;
  lastActivityAt?: string | null;
  dueDate?: string | null;
}

export interface CachedImportDraft {
  id: string;
  type: "GOODS_RECEIPT" | "PRODUCTS" | "SUPPLIERS" | "CUSTOMERS";
  source: "MANUAL" | "FILE_XLSX" | "FILE_CSV" | "CAMERA_OCR";
  title: string;
  supplierName?: string | null;
  supplierId?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  discountAmount: number;
  totalAmount: number;
  paidAmount: number;
  debtAmount: number;
  lines: Array<{
    id: string;
    sku: string;
    productName: string;
    unitName: string;
    quantity: number;
    unitCost: number;
    lineTotal: number;
    lotCode?: string | null;
    expiryDate?: string | null;
    matchedProductId?: string | null;
    matchedUnitId?: string | null;
    confidence: number;
    rawText?: string;
  }>;
  notes?: string | null;
  status: "DRAFT" | "CONFIRMED";
  createdAt: string;
  updatedAt: string;
}

class BLedgerDB extends Dexie {
  products!: EntityTable<CachedProduct, "id">;
  customers!: EntityTable<CachedCustomer, "id">;
  suppliers!: EntityTable<CachedSupplier, "id">;
  sales!: EntityTable<LocalSale, "id">;
  money!: EntityTable<LocalMoneyMovement, "id">;
  syncQueue!: EntityTable<SyncQueueItem, "id">;
  meta!: EntityTable<MetaRecord, "key">;
  importDrafts!: EntityTable<CachedImportDraft, "id">;

  constructor() {
    super("bledger");

    this.version(1).stores({
      products: "id,name,barcode,stockQty",
      customers: "id,name",
      sales: "id,createdAt,paymentMethod,syncStatus",
      money: "id,createdAt,direction,category",
      syncQueue: "id,status,createdAt,type",
      meta: "key",
    });

    this.version(2).stores({
      products: "id,name,barcode,stockQty",
      customers: "id,name",
      suppliers: "id,name",
      sales: "id,createdAt,paymentMethod,syncStatus",
      money: "id,createdAt,direction,category",
      syncQueue: "id,status,createdAt,type",
      meta: "key",
    });

    this.version(3).stores({
      products: "id,name,barcode,stockQty",
      customers: "id,name",
      suppliers: "id,name",
      sales: "id,createdAt,paymentMethod,syncStatus",
      money: "id,createdAt,direction,category",
      syncQueue: "id,status,createdAt,type",
      meta: "key",
    }).upgrade(async (tx) => {
      const products = tx.table("products");
      await products.toCollection().modify((product: CachedProduct) => {
        if (!Array.isArray(product.units)) product.units = [];
        if (product.nextExpiryDate === undefined) product.nextExpiryDate = null;
      });
    });

    this.version(4).stores({
      products: "id,name,barcode,stockQty",
      customers: "id,name",
      suppliers: "id,name",
      sales: "id,createdAt,paymentMethod,syncStatus",
      money: "id,createdAt,direction,category",
      syncQueue: "id,status,createdAt,type",
      meta: "key",
      importDrafts: "id,type,source,status,createdAt",
    });

    this.version(5).stores({
      products: "id,name,barcode,stockQty",
      customers: "id,name",
      suppliers: "id,name",
      sales: "id,createdAt,paymentMethod,syncStatus",
      money: "id,createdAt,direction,category",
      syncQueue: "id,status,createdAt,type,operation,idempotencyKey,[tenantId+storeId+status]",
      meta: "key",
      importDrafts: "id,type,source,status,createdAt",
    }).upgrade(async (tx) => {
      const syncQueue = tx.table("syncQueue");
      await syncQueue.toCollection().modify((item: SyncQueueItem) => {
        if (!item.operation && item.type) item.operation = item.type;
        if (!item.type && item.operation) item.type = item.operation as SyncQueueItem["type"];
        if (item.attemptCount === undefined) item.attemptCount = 0;
        if (!item.idempotencyKey) item.idempotencyKey = item.id;
        if (!item.tenantId) item.tenantId = "default";
        if (!item.storeId) item.storeId = "default";
      });
    });
  }
}

export const db = new BLedgerDB();

export async function purgeLegacyDemoData(): Promise<void> {
  try {
    const isDemoKey = (value: unknown) => {
      const text = String(value ?? "");
      return text.startsWith("demo-") || text.startsWith("LOCAL-demo");
    };

    const containsDemoReference = (value: unknown): boolean => {
      if (value == null) return false;
      if (typeof value === "string") {
        return value.startsWith("demo-") || value.startsWith("LOCAL-demo");
      }
      try {
        const serialized = JSON.stringify(value);
        return serialized.includes('"demo-') || serialized.includes('"LOCAL-demo');
      } catch {
        return false;
      }
    };

    const productKeys = (await db.products.toCollection().primaryKeys()).filter(isDemoKey);
    const customerKeys = (await db.customers.toCollection().primaryKeys()).filter(isDemoKey);
    const supplierKeys = (await db.suppliers.toCollection().primaryKeys()).filter(isDemoKey);
    const saleKeys = (await db.sales.toCollection().primaryKeys()).filter(isDemoKey);
    const moneyKeys = (await db.money.toCollection().primaryKeys()).filter(isDemoKey);

    const syncItems = await db.syncQueue.toArray();
    const syncKeys = syncItems
      .filter((item) => isDemoKey(item.id) || containsDemoReference(item.payload))
      .map((item) => item.id);

    await db.transaction(
      "rw",
      [
        db.products,
        db.customers,
        db.suppliers,
        db.sales,
        db.money,
        db.syncQueue,
        db.meta,
        db.importDrafts,
      ],
      async () => {
        if (productKeys.length) await db.products.bulkDelete(productKeys);
        if (customerKeys.length) await db.customers.bulkDelete(customerKeys);
        if (supplierKeys.length) await db.suppliers.bulkDelete(supplierKeys);
        if (saleKeys.length) await db.sales.bulkDelete(saleKeys);
        if (moneyKeys.length) await db.money.bulkDelete(moneyKeys);
        if (syncKeys.length) await db.syncQueue.bulkDelete(syncKeys);

        for (const key of ["dashboard", "debts", "server-alerts", "last-closing"]) {
          const record = await db.meta.get(key);
          if (record && containsDemoReference(record.value)) {
            await db.meta.delete(key);
          }
        }
      },
    );
  } catch (err) {
    console.warn("Failed to purge legacy demo data:", err);
  }
}

export async function clearAllLocalBusinessData(): Promise<void> {
  try {
    await db.transaction(
      "rw",
      [
        db.products,
        db.customers,
        db.suppliers,
        db.sales,
        db.money,
        db.syncQueue,
        db.meta,
        db.importDrafts,
      ],
      async () => {
        await db.products.clear();
        await db.customers.clear();
        await db.suppliers.clear();
        await db.sales.clear();
        await db.money.clear();
        await db.syncQueue.clear();
        await db.meta.clear();
        await db.importDrafts.clear();
      },
    );
  } catch (err) {
    console.warn("Failed to clear local business data:", err);
  }
}

